/**
 * Results Summary Component
 * 
 * Displays summary of bulk event creation results.
 * Shows successful and failed events with details.
 * 
 * Requirements: 8.1-8.6
 */

import { Modal } from '../../common/Modal';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';

export interface EventCreationResult {
  eventName: string;
  eventDate: string;
  success: boolean;
  error?: string;
}

export interface BatchResults {
  successful: EventCreationResult[];
  failed: EventCreationResult[];
}

export interface ResultsSummaryProps {
  results: BatchResults | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Results Summary Component
 * Requirements: 8.1-8.6
 */
export function ResultsSummary({
  results,
  isOpen,
  onClose,
}: ResultsSummaryProps) {
  if (!results) {
    return null;
  }
  
  const totalEvents = results.successful.length + results.failed.length;
  const successRate = totalEvents > 0
    ? Math.round((results.successful.length / totalEvents) * 100)
    : 0;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Event Creation Results"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Summary Stats */}
        <Card>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {totalEvents}
              </div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {results.successful.length}
              </div>
              <div className="text-sm text-gray-600">Successful</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {results.failed.length}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>
          
          {/* Success Rate Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Success Rate</span>
              <span>{successRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${successRate}%` }}
              />
            </div>
          </div>
        </Card>
        
        {/* Failed Events */}
        {results.failed.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              Failed Events ({results.failed.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {results.failed.map((result, index) => (
                <Card key={index} className="bg-red-50 border border-red-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">
                        {result.eventName}
                      </div>
                      <div className="text-sm text-gray-600">
                        {result.eventDate}
                      </div>
                    </div>
                  </div>
                  {result.error && (
                    <div className="mt-2 text-sm text-red-700">
                      Error: {result.error}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Successful Events */}
        {results.successful.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-green-600 mb-2">
              Successful Events ({results.successful.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {results.successful.map((result, index) => (
                <Card key={index} className="bg-green-50 border border-green-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-gray-900">
                        {result.eventName}
                      </div>
                      <div className="text-sm text-gray-600">
                        {result.eventDate}
                      </div>
                    </div>
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Close Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ResultsSummary;
